import { useDispatch } from 'react-redux';

import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { useLazyGetJobStatusQuery } from '@features/lead-management/queries/LeadManagementQuery';
import useNotify from '@hooks/notify';
import { getLocalStorageItem } from '@utils/storage';

import { updateMetaSyncStatus } from '../metaSyncSlice';
import {
  META_JOB_STATUS,
  META_SYNC_STATUS,
  META_SYNC_TOAST_MESSAGES
} from '../utils/metaIntegrationHelpers';

type SyncCompletionStatus =
  | typeof META_SYNC_STATUS.COMPLETED
  | typeof META_SYNC_STATUS.ERROR
  | typeof META_SYNC_STATUS.PARTIAL_SUCCESS;

interface PollMetaSyncParams {
  syncId: string;
  jobId: string;
  onComplete?: (status: SyncCompletionStatus) => void;
}

const useMetaSyncPolling = () => {
  const dispatch = useDispatch();
  const { createErrorAlert, createSuccessAlert, createWarningAlert } =
    useNotify();
  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );

  const [getJobStatus] = useLazyGetJobStatusQuery();

  const pollMetaSyncStatus = async ({
    syncId,
    jobId,
    onComplete
  }: PollMetaSyncParams): Promise<void> => {
    let pollCount = 0;
    const maxInitialPolls = 4; // 4 polls at 3 seconds interval
    const initialPollInterval = 3000; // 3 seconds
    const subsequentPollInterval = 5000; // 5 seconds
    const maxTotalPolls = 60; // 60 polls at 5 seconds interval
    const startTime = Date.now(); // start time of the polling
    const maxDuration = 5 * 60 * 1000; // 5 minutes

    const pollStatus = async (): Promise<void> => {
      if (pollCount >= maxTotalPolls || Date.now() - startTime > maxDuration) {
        const errorMsg = 'Lead sync timed out. Please try again.';
        dispatch(
          updateMetaSyncStatus({
            id: syncId,
            status: META_SYNC_STATUS.ERROR,
            error: errorMsg
          })
        );
        createErrorAlert(errorMsg);
        onComplete?.(META_SYNC_STATUS.ERROR);
        return;
      }

      try {
        const { data } = await getJobStatus({
          organizationId: organizationId || '',
          jobId
        });

        if (!data) {
          pollCount += 1;
          const interval =
            pollCount < maxInitialPolls
              ? initialPollInterval
              : subsequentPollInterval;
          setTimeout(pollStatus, interval);
          return;
        }

        const { status } = data;

        if (status === META_JOB_STATUS.SUCCESS) {
          dispatch(
            updateMetaSyncStatus({
              id: syncId,
              status: META_SYNC_STATUS.COMPLETED
            })
          );
          createSuccessAlert(META_SYNC_TOAST_MESSAGES.SUCCESS);
          onComplete?.(META_SYNC_STATUS.COMPLETED);
          return;
        }

        if (status === META_JOB_STATUS.FAILED) {
          const errorMsg = data.error || META_SYNC_TOAST_MESSAGES.ERROR;
          dispatch(
            updateMetaSyncStatus({
              id: syncId,
              status: META_SYNC_STATUS.ERROR,
              error: errorMsg
            })
          );
          createErrorAlert(META_SYNC_TOAST_MESSAGES.ERROR, {
            classOverride: 'max-w-xl'
          });
          onComplete?.(META_SYNC_STATUS.ERROR);
          return;
        }

        if (status === META_JOB_STATUS.PARTIAL_SUCCESS) {
          dispatch(
            updateMetaSyncStatus({
              id: syncId,
              status: META_SYNC_STATUS.PARTIAL_SUCCESS
            })
          );
          createWarningAlert(META_SYNC_TOAST_MESSAGES.PARTIAL_SUCCESS);
          onComplete?.(META_SYNC_STATUS.PARTIAL_SUCCESS);
          return;
        }

        if (status === META_JOB_STATUS.PROCESSING) {
          pollCount += 1;
          const interval =
            pollCount < maxInitialPolls
              ? initialPollInterval
              : subsequentPollInterval;
          setTimeout(pollStatus, interval);
          return;
        }

        pollCount += 1;
        const interval =
          pollCount < maxInitialPolls
            ? initialPollInterval
            : subsequentPollInterval;
        setTimeout(pollStatus, interval);
      } catch (error) {
        const errorMsg = 'Failed to find sync status.';
        dispatch(
          updateMetaSyncStatus({
            id: syncId,
            status: META_SYNC_STATUS.ERROR,
            error: errorMsg
          })
        );
        createErrorAlert(errorMsg);
        onComplete?.(META_SYNC_STATUS.ERROR);
      }
    };

    pollStatus();
  };

  return { pollMetaSyncStatus };
};

export default useMetaSyncPolling;
